import { Fragment, type FunctionComponent, useEffect, useState } from 'react'

import { WebhookAuthType, WebhookEvent } from 'src/types/webhook'

import useCreateWebhookMutation from '~/hooks/services/webhooks/mutations/useCreateWebhookMutation'
import useTestWebhookMutation from '~/hooks/services/webhooks/mutations/useTestWebhookMutation'
import useUpdateWebhookMutation from '~/hooks/services/webhooks/mutations/useUpdateWebhookMutation'
import useWebhookQuery from '~/hooks/services/webhooks/queries/useWebhookQuery'
import useWebhooksQuery from '~/hooks/services/webhooks/queries/useWebhooksQuery'

import moment from 'moment'
import { useNavigate, useParams } from 'react-router-dom'

import Button from '../Common/Button'
import Input from '../Common/Input'
import PasswordInput from '../Common/PasswordInput'
import PortalDrawer from '../Common/PortalDrawer'
import Select from '../Common/Select'
import Submit from '../Common/Submit'
import Switch from '../Common/Switch'

const authTypeOptions = Object.values(WebhookAuthType).map((type) => ({ label: type, value: type }))
const webhookEventOptions = Object.values(WebhookEvent).map((event) => ({ label: event, value: event }))

const WebhookDrawer: FunctionComponent = () => {
  const { id } = useParams()
  const navigate = useNavigate()

  // Form state for webhook details
  const [name, setName] = useState('')
  const [url, setUrl] = useState('')
  const [authType, setAuthType] = useState<WebhookAuthType>(WebhookAuthType.None)
  const [authKey, setAuthKey] = useState('')
  const [authValue, setAuthValue] = useState('')
  const [selectedEvents, setSelectedEvents] = useState<WebhookEvent[]>([])
  const [eventsError, setEventsError] = useState('')
  const [enabled, setEnabled] = useState<boolean>(true)
  const [testEvent, setTestEvent] = useState<WebhookEvent>(WebhookEvent.TicketCreated)

  // Get webhooks data and mutations
  const { data: webhooksData } = useWebhooksQuery()
  const {
    data: webhookById,
    refetch: refetchWebhookById,
    isFetching: isFetchingWebhook
  } = useWebhookQuery(id && id !== 'new' ? id : undefined, { staleTime: 0, cacheTime: 0 })
  const { mutateAsync: testWebhook, isLoading: isTesting } = useTestWebhookMutation()
  const { mutateAsync: createWebhook, isLoading: isCreating } = useCreateWebhookMutation()
  const { mutateAsync: updateWebhook, isLoading: isUpdating } = useUpdateWebhookMutation()

  const listWebhook = id ? webhooksData?.find((webhook) => webhook.id === id) : null
  const currentWebhook = webhookById ?? listWebhook ?? null
  const isNewWebhook = id === 'new'

  // Update form state when webhook changes
  useEffect(() => {
    if (currentWebhook) {
      setName(currentWebhook.name || '')
      setUrl(currentWebhook.url || '')
      setAuthType(currentWebhook.authType)
      setAuthKey(currentWebhook.authKey || '')
      setAuthValue(currentWebhook.authValue || '')
      setSelectedEvents(currentWebhook.events || [])
      setEnabled(currentWebhook.enabled)
    } else {
      setName('')
      setUrl('')
      setAuthType(WebhookAuthType.None)
      setAuthKey('')
      setAuthValue('')
      setSelectedEvents([])
      setEnabled(true)
    }
    // Clear any validation errors when webhook changes
    setEventsError('')
  }, [currentWebhook])

  const handleClose = () => {
    // Clear any validation errors when closing
    setEventsError('')
    const currentPath = window.location.pathname
    const newPath = currentPath.substring(0, currentPath.lastIndexOf('/'))
    navigate(newPath)
  }

  const handleSubmit = async (e: React.FormEvent) => {
    e.preventDefault()

    // Validate that at least one event is selected
    if (selectedEvents.length === 0) {
      setEventsError('Please select at least one event for the webhook.')
      return
    }

    // Clear any previous errors
    setEventsError('')

    try {
      if (isNewWebhook) {
        await createWebhook({
          name,
          url,
          enabled,
          authType,
          authKey,
          authValue,
          events: selectedEvents
        })
      } else if (currentWebhook?.id) {
        await updateWebhook({
          id: currentWebhook.id,
          name,
          url,
          enabled,
          authType,
          authKey,
          authValue,
          events: selectedEvents
        })
      }
      handleClose()
    } catch (error) {
      console.error('Failed to save webhook:', error)
    }
  }

  const handleEventToggle = (event: WebhookEvent) => {
    setSelectedEvents((prev) => (prev.includes(event) ? prev.filter((e) => e !== event) : [...prev, event]))
    // Clear error when user selects an event
    if (eventsError) {
      setEventsError('')
    }
  }

  const drawerIsHidden = !id

  const isLoading = isCreating || isUpdating

  // Actions component for the drawer
  const Actions: FunctionComponent = (): JSX.Element => (
    <div className="grid grid-cols-2 gap-2">
      <Button bordered onClick={handleClose} disabled={isLoading}>
        Cancel
      </Button>
      <Submit variant="primary" form="webhookForm" loading={isLoading} loadingText="Saving...">
        {isNewWebhook ? 'Create' : 'Update'}
      </Submit>
    </div>
  )

  return (
    <PortalDrawer
      title={isNewWebhook ? 'New Webhook' : `Edit Webhook`}
      loading={isCreating || isUpdating}
      actions={<Actions />}
      onClose={handleClose}
      hidden={drawerIsHidden}
    >
      <form id="webhookForm" onSubmit={handleSubmit} className="flex flex-col gap-4">
        {!isNewWebhook && <Input type="text" name="id" label="ID" value={currentWebhook?.id || ''} readOnly disabled />}

        <div className="flex items-center justify-between">
          <label className="ml-2 text-th-text-secondary">Enabled</label>
          <Switch id="enabled" checked={enabled} onChange={setEnabled} />
        </div>

        <Input type="text" name="name" label="Name" value={name} onChange={(e) => setName(e.target.value)} placeholder="Enter webhook name" />

        <Input type="url" name="url" label="URL" value={url} onChange={(e) => setUrl(e.target.value)} required placeholder="https://example.com/webhook" />

        <Select
          name="authType"
          label="Auth Type"
          options={authTypeOptions}
          selected={authType}
          onChange={(e) => setAuthType(e.target.value as WebhookAuthType)}
          required
        />

        {authType !== WebhookAuthType.None && (
          <>
            <Input
              type="text"
              name="authKey"
              label={authType === WebhookAuthType.Basic ? 'Username' : 'Header Name'}
              value={authKey}
              onChange={(e) => setAuthKey(e.target.value)}
              placeholder={authType === WebhookAuthType.Basic ? 'Enter username' : 'Enter header name'}
              required
            />

            <PasswordInput
              name="authValue"
              label={authType === WebhookAuthType.Basic ? 'Password' : 'Header Value'}
              value={authValue}
              onChange={(e) => setAuthValue(e.target.value)}
              placeholder={authType === WebhookAuthType.Basic ? 'Enter password' : 'Enter header value'}
              required
            />
          </>
        )}

        <div className="flex flex-col gap-1">
          <label className="ml-2 flex gap-1 text-th-text-secondary">
            Events
            <div aria-hidden="true" className="text-th-error">
              *
            </div>
          </label>
          <div className="space-y-2 pl-4">
            {Object.values(WebhookEvent).map((event) => (
              <div key={event} className="flex items-center justify-between border-b border-th-border pb-2 last:border-b-0">
                <span>{event}</span>
                <Switch id={event} checked={selectedEvents.includes(event)} onChange={() => handleEventToggle(event)} />
              </div>
            ))}
          </div>
          {eventsError && <div className="ml-2 mt-1 text-sm text-th-error">{eventsError}</div>}
        </div>

        {!isNewWebhook && (
          <div className="mt-2 flex items-end gap-2 border-t border-th-border pt-3">
            <div className="flex-1">
              <Select
                name="testEvent"
                label="Test Event"
                options={webhookEventOptions}
                selected={testEvent}
                onChange={(e) => setTestEvent(e.target.value as WebhookEvent)}
                className="w-full"
              />
            </div>
            <Button
              onClick={async (e) => {
                e.preventDefault()
                if (!currentWebhook?.id) return
                const event = testEvent ?? (Object.values(WebhookEvent)[0] as WebhookEvent)
                await testWebhook({ webhookId: currentWebhook.id, event })
              }}
              disabled={isTesting}
              variant="primary"
              className="ml-auto shrink-0"
            >
              {isTesting ? 'Sending...' : 'Send Test'}
            </Button>
          </div>
        )}

        {!isNewWebhook && (
          <div className="mt-2 space-y-2 rounded-lg border border-th-border p-3">
            <div className="flex items-center justify-between">
              <div className="text-sm font-semibold text-th-text-secondary">Last Call Info</div>
              <button
                type="button"
                className="text-th-text-secondary transition-colors hover:text-th-text"
                onClick={() => refetchWebhookById()}
                title="Refresh"
              >
                {isFetchingWebhook ? (
                  <div className="h-4 w-4 animate-spin rounded-full border-2 border-th-text-secondary border-t-transparent" />
                ) : (
                  <svg xmlns="http://www.w3.org/2000/svg" viewBox="0 0 20 20" fill="currentColor" className="h-5 w-5">
                    <path
                      fillRule="evenodd"
                      d="M4 2a1 1 0 011 1v2.126a7.002 7.002 0 0111.601 2.039 1 1 0 11-1.885.666A5.002 5.002 0 006.999 6H9a1 1 0 010 2H4a1 1 0 01-1-1V3a1 1 0 011-1zm12 16a1 1 0 01-1-1v-2.126a7.002 7.002 0 01-11.601-2.039 1 1 0 111.885-.666A5.002 5.002 0 0013.001 14H11a1 1 0 110-2h5a1 1 0 011 1v4a1 1 0 01-1 1z"
                      clipRule="evenodd"
                    />
                  </svg>
                )}
              </button>
            </div>
            <div className="grid grid-cols-1 gap-2 sm:grid-cols-3">
              <div>
                <div className="text-xs text-th-text-secondary">Last Called</div>
                <div className="font-mono text-sm">{currentWebhook?.lastCalled ? moment(currentWebhook.lastCalled).format('DD/MM/YYYY HH:mm') : '-'}</div>
              </div>
              <div>
                <div className="text-xs text-th-text-secondary">Status Code</div>
                <div className="font-mono text-sm">{currentWebhook?.lastResponseStatus ?? '-'}</div>
              </div>
              <div className="sm:col-span-3">
                <div className="text-xs text-th-text-secondary">Details</div>
                <div className="font-mono break-words text-sm">{currentWebhook?.lastResponseDetails || '-'}</div>
              </div>
            </div>
          </div>
        )}
      </form>
    </PortalDrawer>
  )
}

export default WebhookDrawer
